'use client';

import * as React from 'react';
import { Badge } from './Badge';
import { PriceSection } from './PriceSection';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
	CardFooter,
} from '../card';
import { cn } from '@/lib/utils';

interface ServiceCardProps {
	id: string;
	title: string;
	description: string;
	imageUrl: string;
	duration: string;
	frequency: string;
	location: string;
	price: string;
	isPOA?: boolean;
	isPerHour?: boolean;
	isDark?: boolean;
	isBookable?: boolean;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
	id,
	title,
	description,
	imageUrl,
	duration,
	frequency,
	location,
	price,
	isPOA = false,
	isPerHour = false,
	isDark = false,
	isBookable = true,
}) => {
	return (
		<article className='w-full max-w-[350px]'>
			<Card
				className={cn(
					'flex relative flex-col grow rounded-xl min-h-[380px] sm:min-h-[380px] md:min-h-[450px] group py-10',
					isDark ? 'text-white' : 'text-neutral-800',
				)}
			>
				<img
					src={imageUrl}
					alt={title}
					className='object-cover absolute inset-0 size-full rounded-xl'
				/>
				<div
					className={cn(
						'absolute bottom-0 left-0 right-0 md:h-[53%] rounded-xl transition-all duration-300 ease-in-out sm:group-hover:h-[90%] flex flex-col',
						isDark ? 'bg-[#0C1518]/90' : 'bg-gray-100/95',
					)}
				>
					{/* Content area (expands on hover) */}
					<CardContent className='relative px-2.5 py-2.5 sm:p-3 w-full overflow-hidden flex-grow'>
						<div className='w-full'>
							<CardHeader className='flex flex-col w-full p-0'>
								<CardTitle
									className={cn(
										'text-sm sm:text-base font-semibold sm:group-hover:line-clamp-none line-clamp-2',
										isDark ? 'text-white' : 'text-neutral-800',
									)}
								>
									{title}
								</CardTitle>
								<div className='flex flex-wrap gap-1 items-start self-start mt-1'>
									{frequency && <Badge text={frequency} />}
									{location && <Badge text={location} />}
									{duration && <Badge text={duration} />}
								</div>
							</CardHeader>
							<CardDescription
								className={cn(
									'mt-1.5 text-xs leading-tight sm:group-hover:line-clamp-none line-clamp-3',
									isDark ? 'text-gray-300' : 'text-neutral-700',
								)}
							>
								{description}
							</CardDescription>
						</div>
					</CardContent>

					{/* Footer area (stays fixed at bottom) */}
					<div className='mt-auto'>
						<CardFooter className='px-2.5 sm:px-3 py-4 w-full flex flex-col gap-1.5'>
							{isBookable ? (
								<PriceSection
									price={price}
									isPerHour={isPerHour}
									serviceId={id}
									compact={true}
									isPOA={isPOA}
								/>
							) : (
								<div className='text-center text-sm text-red-500 font-semibold'>
									Booking unavailable
								</div>
							)}
							<a
								href={`/services/${id}`}
								className={cn(
									'block w-full text-center rounded-md p-2.5 text-sm font-medium transition-all duration-200',
									isDark
										? 'bg-white/10 text-white hover:bg-white/20'
										: 'bg-neutral-800 text-white hover:bg-neutral-700',
								)}
							>
								View Details
							</a>
						</CardFooter>
					</div>
				</div>
			</Card>
		</article>
	);
};
